/** /mcp-diagnose-runs/:taskId — one client MCP diagnose run, for its review (client MCP plan §5.5,
 *  §7.3 #4; WPS FE-07, backend P4-03).
 *
 *  Read top to bottom: our facts (who asked, about which booking, how the run went, what it cost);
 *  the judge's verdict on the AI-written answer (its note is the judge model's words, quoted); the
 *  review flag and its outcome; the client's question in ONE block marked untrusted — their words,
 *  never instructions; what the client got (the checks' findings, or the released answer); and,
 *  apart, the AI-written answer the judge withheld, which never reached the client. "Record
 *  review…" records what a person found (`console_entity_ai`, the page's own permission). */
import RateReviewIcon from '@mui/icons-material/RateReview';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { formatUsd } from '@/api/aiSpend';
import { ConsoleApiError } from '@/api/client';
import * as runsApi from '@/api/mcpDiagnoseRuns';
import {
  answerText,
  judgeColor,
  judgeLabel,
  mcpDiagnoseRunKeys,
  reviewOutcomeColor,
  reviewPhrase,
  reviewReasonColor,
  reviewReasonLabel,
  runStateColor,
  runStateLabel,
  type McpDiagnoseRunDetail,
} from '@/api/mcpDiagnoseRuns';
import { PageHeader } from '@/components/PageHeader';
import { Field, FieldGrid, SectionCard } from '@/components/ReadView';
import { UntrustedBlock, UntrustedInline, UntrustedLegend, UntrustedText } from '@/components/UntrustedText';
import { operatorActions } from '@/components/ValueActions/identity';
import {
  act,
  CopyableId,
  DetailSubtitle,
  utcTitle,
  ValueActions,
} from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';

import { DiagnosisView } from './DiagnosisView';
import {
  askerActions,
  judgeActions,
  reasonActions,
  reviewQueueHref,
  reviewsAuditHref,
  runActions,
  tenantGrid,
} from './mcpDiagnoseRunLinks';
import { ReviewDialog } from './ReviewDialog';
import { SubjectCell } from './SubjectCell';

export function McpDiagnoseRunPage() {
  const { taskId = '' } = useParams<{ taskId: string }>();
  const query = useQuery({
    queryKey: mcpDiagnoseRunKeys.detail(taskId),
    queryFn: () => runsApi.retrieve(taskId),
    enabled: taskId !== '',
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.isError) {
    const status = query.error instanceof ConsoleApiError ? query.error.status : 0;
    return (
      <Box>
        <PageHeader title="Diagnose run" helpStory="mcp-diagnose-runs" />
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404
            ? 'There is no client diagnose run with this id.'
            : status === 403
              ? 'You can’t see client diagnose runs: it needs the console_entity_ai permission.'
              : `Couldn’t load the run: ${query.error.message}`}
        </Alert>
      </Box>
    );
  }
  return <RunView run={query.data} />;
}

function Moment({ iso }: { iso: string | null }) {
  if (!iso) return null;
  return (
    <ValueActions title={utcTitle(iso)} actions={[act.copy(iso, 'Copy timestamp')]}>
      {formatDate(iso)}
    </ValueActions>
  );
}

function RunView({ run }: { run: McpDiagnoseRunDetail }) {
  const [reviewing, setReviewing] = useState(false);
  const review = run.review;

  const tenantPart = run.tenant ? (
    <ValueActions key="tenant" actions={operatorActions(run.tenant, tenantGrid(run.tenant.id))}>
      {run.tenant.name || run.tenant.code}
    </ValueActions>
  ) : (
    `Tenant #${run.tour_operator_id}`
  );

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        helpStory="mcp-diagnose-runs"
        title={`Diagnose run ${run.task_id.slice(0, 8)}`}
        titleAdornment={
          <Chip
            size="small"
            color={
              !review
                ? 'default'
                : review.state === 'pending'
                  ? 'warning'
                  : reviewOutcomeColor(review.outcome ?? 'ok')
            }
            label={reviewPhrase(review)}
          />
        }
        subtitle={
          <DetailSubtitle
            nowrap
            parts={[
              tenantPart,
              <ValueActions key="when" title={utcTitle(run.created_ts)} actions={[]}>
                {formatDate(run.created_ts)}
              </ValueActions>,
              <ValueActions key="id" actions={runActions(run.task_id)}>
                ID {run.task_id}
              </ValueActions>,
            ]}
          />
        }
        actions={
          <>
            <Button component={RouterLink} to={reviewQueueHref()} size="small">
              The review queue
            </Button>
            <Button variant="contained" startIcon={<RateReviewIcon />} onClick={() => setReviewing(true)}>
              Record review…
            </Button>
          </>
        }
      />

      <Box data-run-part="facts">
        <SectionCard title="The run">
          <FieldGrid>
            <Field
              label="Asked by"
              value={
                run.user_email ? (
                  <ValueActions actions={askerActions({ id: run.user_id, email: run.user_email })}>
                    {run.user_email}
                  </ValueActions>
                ) : null
              }
            />
            <Field label="Subject" value={<SubjectCell subject={run.subject} />} />
            <Field
              label="Run"
              value={
                <Chip
                  size="small"
                  variant="outlined"
                  color={runStateColor(run.state)}
                  label={runStateLabel(run.state)}
                />
              }
            />
            <Field label="The client’s answer" value={answerText(run)} />
            <Field label="Asked" value={<Moment iso={run.created_ts} />} />
            <Field label="Finished" value={run.finished_ts ? <Moment iso={run.finished_ts} /> : null} />
            <Field label="Cost" value={run.cost_usd === null ? null : formatUsd(run.cost_usd)} />
            <Field label="Run id" value={<CopyableId value={run.task_id} label="Copy run id" />} />
          </FieldGrid>
        </SectionCard>
      </Box>

      <Box data-run-part="judge">
        <SectionCard title="The judge">
          {run.judge ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <ValueActions chip actions={judgeActions(run.judge.outcome)}>
                  <Chip
                    size="small"
                    color={judgeColor(run.judge.outcome)}
                    label={judgeLabel(run.judge.outcome)}
                  />
                </ValueActions>
                {run.judge.categories.map((c) => (
                  <Chip key={c} size="small" variant="outlined" label={c} />
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {run.judge.outcome === 'released'
                  ? 'The judge released the AI-written answer: the client got it.'
                  : 'Anything but released withholds the AI-written answer: the client got the checks’ findings instead.'}
              </Typography>
              {run.judge.note && (
                <Box>
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ mr: 1 }}>
                    The judge’s note (written by AI):
                  </Typography>
                  <UntrustedInline text={run.judge.note} />
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No AI-written answer to judge: the run ended without one, so the client got the
              checks’ findings.
            </Typography>
          )}
        </SectionCard>
      </Box>

      <Box data-run-part="review">
        <SectionCard
          title="Review"
          action={
            <Button
              component={RouterLink}
              to={reviewsAuditHref(run.tour_operator_id)}
              size="small"
            >
              Reviews in the audit log
            </Button>
          }
        >
          {review ? (
            <FieldGrid>
              <Field
                label="Review"
                value={
                  <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="body2" component="span">
                      {reviewPhrase(review)}
                    </Typography>
                    <ValueActions chip actions={reasonActions(review.reason)}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={reviewReasonColor(review.reason)}
                        label={reviewReasonLabel(review.reason)}
                      />
                    </ValueActions>
                  </Stack>
                }
              />
              <Field label="Flagged" value={review.flagged_ts ? <Moment iso={review.flagged_ts} /> : null} />
              <Field label="Reviewed by" value={review.reviewed_by_email} />
              <Field label="Reviewed" value={review.reviewed_ts ? <Moment iso={review.reviewed_ts} /> : null} />
              <Field label="Note" value={review.note || null} />
            </FieldGrid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not flagged for review. Any client run may be reviewed by hand.
            </Typography>
          )}
        </SectionCard>
      </Box>

      <Box sx={{ mb: 2 }}>
        <UntrustedBlock title="The client’s question" legend={run.legend}>
          <UntrustedText text={run.client_question} empty="No question was recorded." />
        </UntrustedBlock>
      </Box>

      <Box data-run-part="client-got">
        <SectionCard title="What the client got">
          <UntrustedLegend title="Written by AI" legend={run.legend} />
          <DiagnosisView result={run.diagnosis} />
        </SectionCard>
      </Box>

      {run.withheld && (
        <Box data-run-part="withheld">
          <SectionCard title="The answer the judge withheld">
            <Alert severity="warning" sx={{ mb: 2 }}>
              This AI-written answer never reached the client: the judge withheld it. Check whether
              it leaks anything internal or another company’s data, or tells the reader what to do.
            </Alert>
            <UntrustedLegend title="Written by AI" legend={run.legend} />
            <DiagnosisView result={run.withheld} />
          </SectionCard>
        </Box>
      )}

      {reviewing && <ReviewDialog run={run} onClose={() => setReviewing(false)} />}
    </Box>
  );
}
